/**
 * Autoload next paginated page on scroll (collections, search, blog, etc.).
 * Fetches section HTML and appends items with deduplication.
 */
class CollectionLoadMore extends HTMLElement {
  connectedCallback() {
    this.nextUrl = this.dataset.nextUrl || '';
    this.sectionId = this.dataset.sectionId || '';
    this.gridSelector = this.dataset.grid || '#product-grid';
    this.itemSelector =
      this.dataset.itemSelector || '.grid__item, .blog-articles__article, .article-template__comments-comment';
    this.loading = false;
    this.controller = null;
    this.failCount = 0;

    if (!this.nextUrl || !this.sectionId) {
      this.complete();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadNextPage();
        }
      },
      { rootMargin: '280px 0px', threshold: 0 }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
  }

  getItemSelectors() {
    return this.itemSelector
      .split(',')
      .map((selector) => selector.trim())
      .filter(Boolean);
  }

  elementMatchesAny(element, selectors = this.getItemSelectors()) {
    return selectors.some((selector) => element.matches(selector));
  }

  get gridRoot() {
    return document.querySelector(this.gridSelector);
  }

  getAppendTarget(root) {
    if (!root) return null;

    const selectors = this.getItemSelectors();
    const hasDirectItems = Array.from(root.children).some((child) => this.elementMatchesAny(child, selectors));
    if (hasDirectItems) return root;

    for (const selector of selectors) {
      const item = root.querySelector(selector);
      if (item?.parentElement && root.contains(item.parentElement)) {
        return item.parentElement;
      }
    }

    return root;
  }

  queryItems(container) {
    const selectors = this.getItemSelectors();
    return container.querySelectorAll(selectors.map((selector) => `:scope > ${selector}`).join(', '));
  }

  setBusy(isBusy) {
    this.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  complete() {
    this.observer?.disconnect();
    this.nextUrl = '';
    this.removeAttribute('data-next-url');
    this.setAttribute('hidden', '');
    this.classList.add('is-complete');
    this.setBusy(false);
  }

  collectExistingKeys(container) {
    const keys = new Set();
    this.queryItems(container).forEach((item) => {
      const key = this.itemKey(item);
      if (key) keys.add(key);
    });
    return keys;
  }

  itemKey(item) {
    if (item.dataset.productId) return `product:${item.dataset.productId}`;
    if (item.dataset.resultId) return `result:${item.dataset.resultId}`;
    if (item.dataset.collectionId) return `collection:${item.dataset.collectionId}`;
    if (item.id) return `id:${item.id}`;

    const productId = item.querySelector('[data-product-id]')?.dataset.productId;
    if (productId) return `product:${productId}`;

    const link = item.querySelector('a[href]');
    if (link) {
      const href = link.getAttribute('href').split('?')[0];
      if (href && href !== '#') return `url:${href}`;
    }

    return null;
  }

  buildFetchUrl(nextUrl) {
    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  cleanItem(item) {
    item.classList.remove(
      'collection-grid__item--desktop-only',
      'scroll-trigger',
      'animate--slide-in',
      'scroll-trigger--cancel'
    );
    item.removeAttribute('data-cascade');
    item.style.removeProperty('--animation-order');
  }

  async loadNextPage() {
    if (this.loading || !this.nextUrl) return;

    const root = this.gridRoot;
    const target = this.getAppendTarget(root);
    if (!target) {
      this.complete();
      return;
    }

    this.loading = true;
    this.setBusy(true);
    this.controller?.abort();
    this.controller = new AbortController();
    const startedAt = performance.now();
    const minSpinnerMs = 400;
    const timeoutId = setTimeout(() => this.controller?.abort(), 15000);

    try {
      const response = await fetch(this.buildFetchUrl(this.nextUrl), {
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newRoot = doc.querySelector(this.gridSelector);
      const newTarget = this.getAppendTarget(newRoot);
      if (!newTarget) {
        this.complete();
        return;
      }

      const elapsed = performance.now() - startedAt;
      if (elapsed < minSpinnerMs) {
        await new Promise((resolve) => setTimeout(resolve, minSpinnerMs - elapsed));
      }

      root?.querySelectorAll('.collection-grid__item--desktop-only').forEach((el) => {
        el.classList.remove('collection-grid__item--desktop-only');
      });

      const existing = this.collectExistingKeys(target);
      const fragment = document.createDocumentFragment();
      let appended = 0;

      this.queryItems(newTarget).forEach((item) => {
        this.cleanItem(item);

        const key = this.itemKey(item);
        if (key && existing.has(key)) return;
        if (key) existing.add(key);

        fragment.appendChild(document.importNode(item, true));
        appended += 1;
      });

      if (appended > 0) {
        target.appendChild(fragment);
      }

      this.failCount = 0;

      const nextEl = doc.querySelector('collection-load-more[data-next-url]');
      const nextUrl = nextEl?.dataset?.nextUrl || '';

      if (nextUrl && appended > 0) {
        this.nextUrl = nextUrl;
        this.dataset.nextUrl = nextUrl;
      } else {
        this.complete();
      }

      if (typeof window.konforApplyMobileGrid === 'function') {
        window.konforApplyMobileGrid();
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        this.failCount += 1;
      } else {
        console.error(error);
        this.failCount += 1;
      }
      if (this.failCount >= 3) {
        this.complete();
      }
    } finally {
      clearTimeout(timeoutId);
      this.loading = false;
      if (this.nextUrl) this.setBusy(false);
    }
  }
}

if (!customElements.get('collection-load-more')) {
  customElements.define('collection-load-more', CollectionLoadMore);
}
